import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import {
  canMoveDashboardWidget,
  canRemoveDashboardWidget,
  DASHBOARD_WIDGET_CATALOG,
  DashboardColumnCount,
  DashboardLayout,
  DashboardWidgetId,
  DashboardWidthSpan,
  getDashboardWidget,
  isWidgetVisibleInLayout,
} from '../config/dashboardWidgets';
import type { DashboardWidgetMoveDirection } from '../config/dashboardWidgets';
import { COLORS } from '../constants/theme';
import { DashboardColumnCountSelector } from './DashboardColumnCountSelector';
import { DashboardWidgetReorderButtons } from './DashboardWidgetReorderButtons';
import { DashboardWidthSpanToggle } from './DashboardWidthSpanToggle';

interface DashboardLayoutEditorProps {
  readonly layout: DashboardLayout;
  readonly isMobileViewport: boolean;
  readonly onAddWidget: (widgetId: DashboardWidgetId) => void;
  readonly onRemoveWidget: (widgetId: DashboardWidgetId) => void;
  readonly onMoveWidget: (widgetId: DashboardWidgetId, direction: DashboardWidgetMoveDirection) => void;
  readonly onSetWidthSpan: (widgetId: DashboardWidgetId, widthSpan: DashboardWidthSpan) => void;
  readonly onSetColumnCount: (columnCount: DashboardColumnCount) => void;
}

export const DashboardLayoutEditor: React.FC<DashboardLayoutEditorProps> = ({
  layout,
  isMobileViewport,
  onAddWidget,
  onRemoveWidget,
  onMoveWidget,
  onSetWidthSpan,
  onSetColumnCount,
}) => {
  const orderedWidgets = [...layout.widgets].sort((left, right) => left.order - right.order);
  const availableWidgets = DASHBOARD_WIDGET_CATALOG.filter(
    (definition) => !isWidgetVisibleInLayout(layout, definition.id)
  );
  const canRemove = canRemoveDashboardWidget(layout);
  const allowWidthSpanControls = layout.columnCount === 2 && !isMobileViewport;
  const allowHorizontalMove = allowWidthSpanControls;

  return (
    <View style={styles.card}>
      <DashboardColumnCountSelector
        columnCount={layout.columnCount}
        onChange={onSetColumnCount}
        isMobileViewport={isMobileViewport}
      />

      <Text style={styles.title}>Widgets</Text>
      <Text style={styles.subtitle}>
        Add, remove and resize here. Drag a widget onto another slot in the preview;
        the layout shape stays put. Arrows move content one slot up, down, left or right.
      </Text>

      {orderedWidgets.map((widget, index) => {
        const definition = getDashboardWidget(widget.id);
        if (!definition) {
          return null;
        }

        const removeDisabled = !canRemove;

        return (
          <View key={widget.id} style={styles.row}>
            <Text style={styles.orderIndex}>{index + 1}</Text>
            <View style={styles.labelBlock}>
              <Text style={styles.widgetTitle} numberOfLines={2}>
                {definition.title}
              </Text>
              <Text style={styles.onDashboardLabel}>On dashboard</Text>
            </View>
            <View style={styles.controls}>
              <DashboardWidthSpanToggle
                widgetTitle={definition.title}
                widthSpan={widget.widthSpan}
                disabled={!allowWidthSpanControls}
                onChange={(widthSpan) => onSetWidthSpan(widget.id, widthSpan)}
              />
              <DashboardWidgetReorderButtons
                widgetTitle={definition.title}
                canMoveUp={canMoveDashboardWidget(layout, widget.id, 'up', {
                  forceSingleColumn: isMobileViewport,
                })}
                canMoveDown={canMoveDashboardWidget(layout, widget.id, 'down', {
                  forceSingleColumn: isMobileViewport,
                })}
                canMoveLeft={
                  allowHorizontalMove &&
                  canMoveDashboardWidget(layout, widget.id, 'left', {
                    forceSingleColumn: isMobileViewport,
                  })
                }
                canMoveRight={
                  allowHorizontalMove &&
                  canMoveDashboardWidget(layout, widget.id, 'right', {
                    forceSingleColumn: isMobileViewport,
                  })
                }
                onMoveUp={() => onMoveWidget(widget.id, 'up')}
                onMoveDown={() => onMoveWidget(widget.id, 'down')}
                onMoveLeft={
                  allowHorizontalMove ? () => onMoveWidget(widget.id, 'left') : undefined
                }
                onMoveRight={
                  allowHorizontalMove ? () => onMoveWidget(widget.id, 'right') : undefined
                }
              />
              <TouchableOpacity
                style={[styles.removeButton, removeDisabled && styles.buttonDisabled]}
                onPress={() => onRemoveWidget(widget.id)}
                disabled={removeDisabled}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Remove ${definition.title}`}
                accessibilityState={{ disabled: removeDisabled }}
              >
                <Text style={styles.removeButtonText}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        );
      })}

      {availableWidgets.length > 0 ? (
        <>
          <Text style={styles.availableTitle}>Available widgets</Text>
          {availableWidgets.map((definition) => (
            <View key={definition.id} style={styles.row}>
              <View style={styles.labelBlock}>
                <Text style={styles.availableTitleText} numberOfLines={2}>
                  {definition.title}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => onAddWidget(definition.id)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Add ${definition.title}`}
              >
                <Text style={styles.addButtonText}>Add</Text>
              </TouchableOpacity>
            </View>
          ))}
        </>
      ) : null}

      {!canRemove ? (
        <Text style={styles.minHint}>At least one widget must stay on the dashboard.</Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: COLORS.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#F4F4F4',
    padding: 16,
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#222',
    marginBottom: 4,
    fontFamily: 'Montserrat_700Bold',
  },
  subtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginBottom: 12,
    fontFamily: 'Montserrat_400Regular',
  },
  availableTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.textSecondary,
    marginTop: 12,
    marginBottom: 4,
    fontFamily: 'Montserrat_600SemiBold',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  orderIndex: {
    width: 20,
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.primary,
    fontFamily: 'Montserrat_700Bold',
  },
  labelBlock: {
    flex: 1,
    minWidth: 140,
  },
  widgetTitle: {
    fontSize: 13,
    color: '#374151',
    fontFamily: 'Montserrat_500Medium',
  },
  availableTitleText: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_400Regular',
  },
  onDashboardLabel: {
    marginTop: 2,
    fontSize: 11,
    color: COLORS.primary,
    fontFamily: 'Montserrat_600SemiBold',
    fontWeight: '600',
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-end',
  },
  addButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primary,
    backgroundColor: COLORS.white,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  addButtonText: {
    fontSize: 13,
    color: COLORS.primary,
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  removeButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
    backgroundColor: '#FEF2F2',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  removeButtonText: {
    fontSize: 13,
    color: COLORS.error,
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  minHint: {
    marginTop: 12,
    fontSize: 12,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_400Regular',
  },
});
